import { escapeRegExp, isEmpty, isNil } from "lodash";
import type { IProcessOptionalString } from "./process-things";
import { getProcessString } from "./process-things";
import { decodeStringLiteral, toStringLiteral } from "./string-literal";
import type { nil } from "./type-utils";

/** A node of a process model (the model itself, an area, a phase or a task): a technical id plus a display name. */
export interface ProcessModelNamedNode {
  modelId: string;
  displayName?: IProcessOptionalString[];
}

const MODEL_ID_LIST_SEPARATOR = ",";
const QUOTES = ["'", '"'];

/**
 * The display names of the given nodes by their modelId: in the given language, otherwise in de-DE, otherwise in any.
 * Nodes without any display name are left out, so callers keep showing their modelId.
 */
export function getModelNamesById(nodes: readonly ProcessModelNamedNode[], language: string): Map<string, string> {
  const entries = nodes
    .filter(({ modelId }) => !isEmpty(modelId))
    .map(({ modelId, displayName }) => ({ modelId, name: getDisplayName(displayName, language) }))
    .filter(({ name }) => !isNil(name))
    .map(({ modelId, name }): [string, string] => [modelId, name]);

  return new Map(entries);
}

/** The display name of a single node (see {@link getModelNamesById}), or its modelId when it has no name at all. */
export function getModelName(node: ProcessModelNamedNode, language: string): string {
  return getDisplayName(node.displayName, language) ?? node.modelId;
}

/**
 * Replaces the known modelIds of a comma-separated list (the `ids` of an error message) with their display names.
 * Everything else stays as it is: a missing or mistyped node has no name, and a malformed id must stay visible.
 */
export function replaceModelIdList(ids: string, namesById: ReadonlyMap<string, string>): string {
  return ids
    .split(MODEL_ID_LIST_SEPARATOR)
    .map((token) => {
      const id = token.trim();
      const name = namesById.get(id);
      return isNil(name) ? token : token.replace(id, () => name);
    })
    .join(MODEL_ID_LIST_SEPARATOR);
}

/**
 * Replaces the quoted modelIds of a text with their display names, keeping the quotes.
 * TypeScript diagnostics quote the task ids of an expression, e.g. `Did you mean 'task-check-documents'?`.
 */
export function replaceQuotedModelIds(text: string, namesById: ReadonlyMap<string, string>): string {
  const ids = Array.from(namesById.keys()).filter((id) => !isEmpty(id));
  if (isEmpty(text) || isEmpty(ids)) {
    return text;
  }

  // Matching the known ids only, not any quoted token, keeps an apostrophe ("can't") from pairing with an id's quote
  const alternatives = ids.map((id) => escapeRegExp(id)).join("|");
  const quotedIdPattern = new RegExp(`(["'])(${alternatives})\\1`, "g");
  return text.replace(quotedIdPattern, (_token, quote: string, id: string) => `${quote}${namesById.get(id)}${quote}`);
}

function getDisplayName(displayName: IProcessOptionalString[] | nil, language: string): string | null {
  const nonEmptyNames = (displayName ?? []).filter(({ value }) => !isEmpty(value?.trim()));
  return getProcessString(nonEmptyNames, language, "de-DE")?.value ?? null;
}

/** The modelIds a text quotes, see {@link findQuotedModelIds}. */
export type QuotedModelIdFinder = (text: string | nil) => string[];

/**
 * The given modelIds that an expression quotes as string literals, e.g. the tasks it refers to by
 * `'task-check-documents'`, in the order of the given ids. A literal counts by the value it stands for, so
 * `'task\'s-name'` quotes the modelId `task's-name`. Like {@link replaceQuotedModelIds} it only looks for the known
 * ids, so other quoted strings (role names, template names, an apostrophe) never count.
 */
export function findQuotedModelIds(text: string | nil, modelIds: Iterable<string>): string[] {
  return createQuotedModelIdFinder(modelIds)(text);
}

/**
 * {@link findQuotedModelIds} for one set of ids, whose pattern is built once: for looking for the same ids in many texts,
 * e.g. in every expression of every task a copy plans, which it would otherwise build anew for each of them.
 */
export function createQuotedModelIdFinder(modelIds: Iterable<string>): QuotedModelIdFinder {
  const ids = Array.from(modelIds).filter((id) => !isEmpty(id));
  if (isEmpty(ids)) {
    return () => [];
  }

  const quotedIdPattern = createModelIdLiteralPattern(ids);
  return (text: string | nil): string[] => {
    if (isEmpty(text)) {
      return [];
    }

    // matchAll runs on a copy of the pattern, so its position never carries over from one text to the next
    const foundIds = new Set(Array.from(text.matchAll(quotedIdPattern), ([literal]) => decodeStringLiteral(literal)));
    return ids.filter((id) => foundIds.has(id));
  };
}

/**
 * Renames the modelIds an expression quotes as string literals (see {@link findQuotedModelIds}), keeping each literal's
 * quote and escaping the new id for it. Unlike {@link replaceQuotedModelIds}, which names ids in messages, it writes code.
 */
export function renameQuotedModelIds(expression: string, newIdsByOldId: ReadonlyMap<string, string>): string {
  const ids = Array.from(newIdsByOldId.keys()).filter((id) => !isEmpty(id));
  if (isEmpty(expression) || isEmpty(ids)) {
    return expression;
  }

  // A callback, not a replacement string: modelIds may contain `$`, which String.replace would interpret
  return expression.replace(createModelIdLiteralPattern(ids), (literal: string) =>
    toStringLiteral(newIdsByOldId.get(decodeStringLiteral(literal)), literal[0])
  );
}

/**
 * Matches the given ids as string literals in either quote, written as {@link toStringLiteral} writes them. The closing
 * quote must follow the id, so an id that starts with another one is still matched in full.
 */
function createModelIdLiteralPattern(ids: string[]): RegExp {
  const literalsByQuote = QUOTES.map((quote) => {
    const contents = ids.map((id) => escapeRegExp(toStringLiteral(id, quote).slice(1, -1)));
    return `${quote}(?:${contents.join("|")})${quote}`;
  });
  return new RegExp(literalsByQuote.join("|"), "g");
}
